import { createContext, useContext, useEffect, useMemo, useState } from "react";

/**
 * Display presets. Three knobs decide how much of the workbench an operator
 * meets at once:
 *
 * - depth    how much is open before the operator asks for it
 * - tone     whether settled state is allowed to use color, or only attention is
 * - density  the vertical rhythm of rows, sections and panels
 *
 * Change a preset here and the whole product follows. Nothing else reads the
 * knobs directly: CSS keys off the data attributes, React reads `useDisplay()`.
 */

export type DisplayDepth = "guided" | "expert";
export type DisplayTone = "quiet" | "full";
export type DisplayDensity = "comfortable" | "compact";

export type DisplaySettings = {
  depth: DisplayDepth;
  tone: DisplayTone;
  density: DisplayDensity;
};

export type DisplayPreset = DisplaySettings & {
  label: string;
  summary: string;
};

export const displayPresets = {
  /** A first migration, or a route the operator has never run. Nothing is open. */
  guided: {
    label: "Guided",
    summary: "One decision at a time. Graphs, checksums and structure controls stay closed.",
    depth: "guided",
    tone: "quiet",
    density: "comfortable",
  },
  /** Someone who runs migrations weekly and wants the whole workbench open. */
  operator: {
    label: "Operator",
    summary: "The full workbench open, with color still reserved for what needs a human.",
    depth: "expert",
    tone: "quiet",
    density: "comfortable",
  },
  /** Reviewing or signing off on someone else's plan: every state, tightly packed. */
  audit: {
    label: "Audit",
    summary: "Every state and identifier visible at maximum density for review.",
    depth: "expert",
    tone: "full",
    density: "compact",
  },
} satisfies Record<string, DisplayPreset>;

export type DisplayPresetId = keyof typeof displayPresets;

export const defaultDisplayPreset: DisplayPresetId = "guided";

const STORAGE_KEY = "prosights-migrate-display-preset";

function isPresetId(value: unknown): value is DisplayPresetId {
  return typeof value === "string" && value in displayPresets;
}

/**
 * Resolution order is deliberate: a `?display=` link wins so a specific
 * migration can be handed over already tuned, then the operator's own choice,
 * then the deployment default, then guided.
 */
export function resolveDisplayPreset(): DisplayPresetId {
  const fromUrl = new URLSearchParams(window.location.search).get("display");
  if (isPresetId(fromUrl)) {
    localStorage.setItem(STORAGE_KEY, fromUrl);
    return fromUrl;
  }
  const stored = localStorage.getItem(STORAGE_KEY);
  if (isPresetId(stored)) return stored;
  const fromEnv = import.meta.env.VITE_DISPLAY_PRESET;
  if (isPresetId(fromEnv)) return fromEnv;
  return defaultDisplayPreset;
}

type DisplayContextValue = DisplayPreset & {
  id: DisplayPresetId;
  /** True when a surface may open detail without being asked. */
  opensByDefault: boolean;
  setPreset: (id: DisplayPresetId) => void;
};

const DisplayContext = createContext<DisplayContextValue | null>(null);

export function DisplayProvider({ children }: { children: React.ReactNode }) {
  const [id, setId] = useState<DisplayPresetId>(resolveDisplayPreset);
  const preset = displayPresets[id];

  useEffect(() => {
    const root = document.documentElement;
    root.dataset.displayDepth = preset.depth;
    root.dataset.displayTone = preset.tone;
    root.dataset.displayDensity = preset.density;
  }, [preset]);

  const value = useMemo<DisplayContextValue>(
    () => ({
      ...preset,
      id,
      opensByDefault: preset.depth === "expert",
      setPreset: (next) => {
        localStorage.setItem(STORAGE_KEY, next);
        setId(next);
      },
    }),
    [id, preset],
  );

  return <DisplayContext.Provider value={value}>{children}</DisplayContext.Provider>;
}

export function useDisplay() {
  const value = useContext(DisplayContext);
  if (!value) throw new Error("useDisplay must be used inside DisplayProvider");
  return value;
}
